import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useApplyFinancingOffer } from '@/components/composed/financing/hooks'
import { useApplyMFOFinancingOffer } from '@/components/composed/financing/hooks/useApplyMFOFinancingOffer'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { FormValuesFormatter } from '@/constants/financing'
import type { IFinancingOfferFormValues } from '@/logic/financing/interfaces'
import { getProfile } from '@/logic/profile/reducer'
import type { RootState } from '@/redux/interfaces'
import { getApplyingFinancingOfferFormInitialValues } from '@/utils/getApplyingFinancingOfferFormInitialValues'
import type { TUseApplyingFinancingOfferFormProps } from '@/utils/hooks/applyingFinancingOffer/types'
import { useRegionsMicrocreditOrganizations } from '@/utils/hooks/applyingFinancingOffer/useRegionsMicrocreditOrganizations'
import { isApplyingOfferRequestBody } from './typeguard'

type TApplyingFinancingOfferFormOptions = {
  formValues: Partial<IFinancingOfferFormValues>
}

const mapState = (state: RootState) => ({
  profile: getProfile()(state),
})

export const useApplyingFinancingOfferForm = ({
  offerType,
  productId,
  shouldSkipSuccessModal,
  onSuccess,
  onError,
  requestMapper,
  isCustomFormattedRequestBody = false,
}: TUseApplyingFinancingOfferFormProps) => {
  const dispatch = useDispatch()
  const { profile } = useSelector(mapState)
  const { isAuthenticated } = useAuthenticateUser()

  const { applyFinancingOffer, isSuccess: isApplyFinancingOfferSuccess } = useApplyFinancingOffer()
  const { applyMFOFianncingOffer, isSuccess: isApplyMFOFinancingOfferSuccess } = useApplyMFOFinancingOffer()

  const { isMFOProduct } = useRegionsMicrocreditOrganizations({
    productId,
  })
  const [options, setOptions] = useState<TApplyingFinancingOfferFormOptions>({ formValues: {} })

  useEffect(() => {
    if (Object.keys(options.formValues || {}).length > 0 && isAuthenticated) {
      const initialValues = getApplyingFinancingOfferFormInitialValues({
        offerType,
        productId,
        profile,
        formValues: options.formValues,
      })

      const body = isCustomFormattedRequestBody
        ? initialValues
        : FormValuesFormatter[offerType]?.(initialValues as IFinancingOfferFormValues) || initialValues

      if (isApplyingOfferRequestBody(body)) {
        const mappedBody = requestMapper?.(body) ?? body

        let result

        if (isMFOProduct) {
          result = applyMFOFianncingOffer({
            body: mappedBody,
            withSuccessModal: !shouldSkipSuccessModal,
          })
        } else {
          result = applyFinancingOffer({ body: mappedBody, offerType, withSuccessModal: !shouldSkipSuccessModal })
        }

        result.then(fundingRequests => onSuccess?.(fundingRequests)).catch(() => onError?.())
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerType, options, isAuthenticated])

  return {
    dispatch,
    profile,
    formValues: options.formValues,
    isSuccess: isApplyFinancingOfferSuccess || isApplyMFOFinancingOfferSuccess,
    setOptions,
  }
}
